/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-get, ember/no-mixins, ember/require-computed-property-dependencies */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import GroupEditMixin from 'linced-app/mixins/group-edit';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import SearchMode from '@linc-technologies/linced-common/enums/person-option-search-mode';
import NSWStatus from '@linc-technologies/linced-common/enums/nsw-status';
import {
  filterBy,
  findBy,
  mapBy,
  rejectBy,
  sortBy,
  uniq,
} from '@linc-technologies/linced-common/utils/arrays';
import moment from 'moment';
import { tagName } from '@ember-decorators/component';

@tagName('')
export default class GroupList extends Component.extend(GroupEditMixin) {
  @service
  session;

  @service
  personOptions;

  @service
  schoolSchema;

  @service
  strings;

  @service
  router;

  GroupType = GroupType;
  SearchMode = SearchMode;

  // initMode() below overwrites this from `groupsModes.<filterValue>` during
  // init(), and setDefaultMode() rewrites it from the same source.
  @tracked
  defaultMode = null;

  @alias('personOptions.groupsModes')
  groupsModes;

  @tracked
  mode = SearchMode.GRID;

  @computed('strings.homeroom')
  get filterValues() {
    return Object.freeze([
      { value: 'my', caption: 'My' },
      { value: 'shared', caption: 'Shared' },
      { value: 'auto', caption: 'Auto' },
      { value: 'class', caption: this.strings.homeroom },
    ]);
  }

  @tracked filterValue = 'my';

  @computed('groups.@each.status', 'filterValue')
  get filteredGroups() {
    let groupType = GroupType.GROUP;
    switch (this.filterValue) {
      case 'class':
        groupType = GroupType.HOMEROOM;
        break;
      case 'auto':
        groupType = GroupType.AUTO;
        break;
      case 'shared':
        groupType = GroupType.SHARED;
        break;
    }

    // My Groups - filter down to only those which the current user is associated with.
    let groups = this.groups.filter((group) => {
      if (group.type !== GroupType.GROUP) {
        return true;
      }
      return !!findBy(
        group.currentStaff,
        'personId',
        this.get('session.person.id'),
      );
    });

    // Filter by specified group type
    groups = filterBy(groups, 'type', groupType);

    // Inactive groups - filter to only those which are inactive
    if (this.filterValue === 'inactive') {
      return filterBy(groups, 'status', NSWStatus.INACTIVE);
    }

    // Filter out inactive groups
    groups = rejectBy(groups, 'status', NSWStatus.INACTIVE);

    // Filter by specified group type
    return groups;
  }

  @computed('groups.@each.{type,currentStaff}', 'session.personId')
  get myHomerooms() {
    return this.groups.filter((group) => {
      return (
        group.type === GroupType.HOMEROOM &&
        findBy(group.currentStaff, 'personId', this.session.personId)
      );
    });
  }

  @computed('filterValue', 'myHomerooms.length')
  get showMyHomerooms() {
    return this.filterValue === 'my' && !isEmpty(this.myHomerooms);
  }

  @computed('filteredGroups.@each.grouping')
  get filteredGroupings() {
    return uniq(mapBy(sortBy(this.filteredGroups, 'grouping'), 'grouping'));
  }

  groupTypeLocked = true;
  displayYear = moment().year();
  onGroupCreated() {}
  onGroupDeleted() {}

  init() {
    super.init(...arguments);
    this.initMode();
  }

  // initMode initialises the mode based on the user's preference for this
  // group type.
  initMode() {
    let defaultMode = this.get(`groupsModes.${this.filterValue}`);
    this.mode = defaultMode;
    this.defaultMode = defaultMode;

    if (this.filterValue === 'auto' && !this.schoolSchema.schemaFetched) {
      // Trigger a schema load so that field-based autogroups can pull the
      // field labels.
      this.schoolSchema.getPeopleSchema();
    }
  }

  @action
  changeFilter(newFilter) {
    this.filterValue = newFilter;
    this.initMode();
  }

  @action
  filterOtherGroups(groupings, group) {
    return !findBy(groupings, 'value', group.get('grouping'));
  }

  @action
  setMode(mode) {
    this.mode = mode;
  }

  @action
  setDefaultMode() {
    this.personOptions.setGroupsMode(this.filterValue, this.mode).then(() => {
      // Once resolved, update our local default to the new value.
      // This will still be the old value if there was an error, so the
      // button to save as default will still be present.
      this.defaultMode = this.get(`groupsModes.${this.filterValue}`);
    });
  }
}
